import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  format, addMonths, subMonths, startOfMonth, endOfMonth, 
  startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, 
  isSameDay, isToday, isBefore, startOfDay 
} from 'date-fns'
import { 
  Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, 
  Check, ArrowLeft, Loader2, MapPin, 
  Video, Briefcase, Info
} from 'lucide-react'

// --- Constants & Types --- //

// API Configuration
const API_BASE_URL = 'https://api.mernaki.de/api'

// Default to the admin user if none provided (for demo/main site)
const DEFAULT_USER_USERNAME = 'mernaki' // Will be resolved to user ID via API

interface Slot {
  startTs: string
  endTs: string
}

interface FormQuestion {
  id: string
  label: string
  type: string
  placeholder?: string
  required: boolean
  options?: string[]
  order: number
}

interface MeetingType {
  id: string
  title: string
  description: string
  durationMinutes: number
  locationType: string
  locationValue?: string
  amount?: number
  currency?: string
  smartFormId?: string
  metadata?: {
    formQuestions?: FormQuestion[]
  }
}

interface NativeSchedulerProps {
  meetingTypeId?: string
  userId?: string
  username?: string // Alternative to userId - will be resolved via API
  prefersReducedMotion?: boolean
}

// --- Components --- //

const Button = ({ 
  children, onClick, variant = 'primary', className = '', disabled = false, icon: Icon 
}: any) => (
  <motion.button
    whileHover={!disabled ? { scale: 1.02 } : {}}
    whileTap={!disabled ? { scale: 0.98 } : {}}
    onClick={onClick}
    disabled={disabled}
    className={`
      relative flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200
      ${variant === 'primary' 
        ? 'bg-gray-900 text-white shadow-lg shadow-gray-200 hover:shadow-xl hover:bg-black' 
        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {children}
    {Icon && <Icon className="w-4 h-4" />}
  </motion.button>
)



// --- Main NativeScheduler --- //

export const NativeScheduler: React.FC<NativeSchedulerProps> = ({
  meetingTypeId: initialMeetingTypeId,
  userId: providedUserId,
  username: providedUsername
}) => {
  // State
  const [step, setStep] = useState<'types' | 'date' | 'form' | 'success'>('types')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Data
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([])
  const [owner, setOwner] = useState<any>(null)
  const [selectedType, setSelectedType] = useState<MeetingType | null>(null)
  const [smartForm, setSmartForm] = useState<any>(null)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null)
  const [slots, setSlots] = useState<Slot[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [_formLoading, setFormLoading] = useState(false)
  
  // Form Data
  const [formData, setFormData] = useState<any>({ name: '', email: '', answers: {} })
  const [formErrors, setFormErrors] = useState<any>({})

  // Constants
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // --- Effects --- //

  useEffect(() => {
    resolveUserIdAndLoadData()
  }, [initialMeetingTypeId, providedUserId, providedUsername])

  useEffect(() => {
    if (selectedType) {
      if (selectedDate) {
        loadSlots(selectedDate)
      }
      if (selectedType.smartFormId) {
        loadSmartForm(selectedType.smartFormId)
      } else {
        setSmartForm(null)
      }
    }
  }, [selectedType, selectedDate])

  // --- Data Loading --- //

  const resolveUserIdAndLoadData = async () => {
    try {
      setLoading(true)
      
      let resolvedUserId = providedUserId
      
      // If no userId provided, try to resolve from username
      if (!resolvedUserId) {
        const usernameToResolve = providedUsername || DEFAULT_USER_USERNAME
        try {
          const res = await fetch(`${API_BASE_URL}/users/by-username/${usernameToResolve}`)
          if (res.ok) {
            const data = await res.json()
            resolvedUserId = data.userId
          }
        } catch (err) {
          console.warn('Failed to resolve username to userId:', err)
        }
      }
      
      if (!resolvedUserId) {
        throw new Error('Unable to resolve user ID')
      }
      
      await loadInitialData(resolvedUserId)
    } catch (err) {
      setError('Unable to load scheduling information. Please try again.')
      setLoading(false)
    }
  }

  const loadSmartForm = async (formId: string) => {
    try {
      setFormLoading(true)
      const res = await fetch(`${API_BASE_URL}/forms/${formId}`)
      if (!res.ok) throw new Error('Failed to load form')
      const data = await res.json()
      setSmartForm(data)
    } catch (err) {
      console.error('Error loading smart form:', err)
      // Fallback or error state? For now just log it.
    } finally {
      setFormLoading(false)
    }
  }

  const loadInitialData = async (resolvedUserId: string) => {
    try {
      // If ID provided, fetch specific type
      if (initialMeetingTypeId) {
        const res = await fetch(`${API_BASE_URL}/meeting-types/${initialMeetingTypeId}`)
        if (!res.ok) throw new Error('Meeting type not found')
        const type = await res.json()
        setSelectedType(type)
        if (type.owner) setOwner(type.owner)
        setStep('date')
      } else {
        // Fetch all types for user
        const res = await fetch(`${API_BASE_URL}/meeting-types/user/${resolvedUserId}`)
        if (!res.ok) throw new Error('Failed to load meeting types')
        const data = await res.json()
        setMeetingTypes(data.meetingTypes || [])
        setOwner(data.user)
        setStep('types')
      }
    } catch (err) {
      throw err // Re-throw to be handled by resolveUserIdAndLoadData
    } finally {
      setLoading(false)
    }
  }

  const loadSlots = async (date: Date) => {
    if (!selectedType) return
    setSlotsLoading(true)
    try {
      const start = startOfDay(date)
      const end = new Date(date)
      end.setHours(23, 59, 59, 999)
      
      const res = await fetch(
        `${API_BASE_URL}/availability/slots?meetingTypeId=${selectedType.id}&from=${start.toISOString()}&to=${end.toISOString()}&tz=${userTimezone}`
      )
      const data = await res.json()
      setSlots(data.slots || [])
    } catch (err) {
      console.error(err)
    } finally {
      setSlotsLoading(false)
    }
  }

  const submitBooking = async () => {
    // Validate
    const errors: any = {}
    if (!formData.name) errors.name = 'Required'
    if (!formData.email) errors.email = 'Required'
    
    // Validate custom questions
    const questions = smartForm?.fields || selectedType?.metadata?.formQuestions || []
    
    questions.forEach((q: FormQuestion) => {
      if (q.required && !formData.answers[q.id]) {
        errors[q.id] = 'Required'
      }
    })

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    try {
      setLoading(true)
      const res = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetingTypeId: selectedType?.id,
          startTs: selectedSlot?.startTs,
          candidate: {
            name: formData.name,
            email: formData.email,
            customAnswers: formData.answers
          },
          timezone: userTimezone
        })
      })

      if (!res.ok) throw new Error('Booking failed')
      setStep('success')
    } catch (err) {
      setError('Booking failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // --- Render Helpers --- //

  const renderCalendar = () => {
    const start = startOfWeek(startOfMonth(currentMonth))
    const end = endOfWeek(endOfMonth(currentMonth))
    const days = eachDayOfInterval({ start, end })

    return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-xl font-bold text-gray-900">{format(currentMonth, 'MMMM yyyy')}</h3>
          <div className="flex gap-2">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2.5 hover:bg-gray-100 rounded-xl text-gray-900 transition-colors border border-gray-100 btn-premium"><ChevronLeft className="w-5 h-5"/></button>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2.5 hover:bg-gray-100 rounded-xl text-gray-900 transition-colors border border-gray-100 btn-premium"><ChevronRight className="w-5 h-5"/></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-y-4 mb-4">
          {['S','M','T','W','T','F','S'].map(d => <div key={d} className="text-center text-sm font-bold text-gray-400 uppercase tracking-widest">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, i) => {
            const isSelected = selectedDate && isSameDay(day, selectedDate)
            const isTodayDate = isToday(day)
            const isDisabled = isBefore(day, startOfDay(new Date()))
            const isCurrentMonth = isSameMonth(day, currentMonth)

            return (
              <button
                key={i}
                disabled={isDisabled}
                onClick={() => { setSelectedDate(day); setSelectedSlot(null) }}
                className={`
                  aspect-square rounded-2xl flex items-center justify-center text-base font-semibold transition-all relative
                  ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
                  ${isDisabled ? 'opacity-20 cursor-not-allowed' : 'hover:bg-gray-100'}
                  ${isSelected ? '!bg-black !text-white shadow-lg shadow-gray-200 scale-105 z-10' : ''}
                  ${!isSelected && isTodayDate ? 'bg-gray-50 text-black border border-gray-200' : ''}
                `}
              >
                {format(day, 'd')}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // --- Main Render --- //

  if (loading && !selectedType && meetingTypes.length === 0) {
    return (
      <div className="w-full h-96 flex flex-col items-center justify-center gap-4 text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin" />
        <p className="text-sm font-medium">Loading Scheduler...</p>
      </div>
    )
  }

  return (
    <div className="w-full max-w-5xl mx-auto bg-white rounded-[2.5rem] shadow-2xl border border-white/20 overflow-hidden flex flex-col md:flex-row h-[700px] ring-1 ring-gray-900/5">
      
      {/* LEFT PANEL - Premium Info */}
      <motion.div 
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="hidden lg:flex w-[35%] bg-[#0A0A0A] text-white p-10 flex-col justify-between relative overflow-hidden h-full flex-shrink-0"
      >
        {/* Decorative background elements - Subtler */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gray-800/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gray-800/20 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2 pointer-events-none" />

        <div className="relative z-10">
           {/* Logo / Brand */}
           <motion.div 
             initial={{ y: 20, opacity: 0 }}
             animate={{ y: 0, opacity: 1 }}
             transition={{ delay: 0.2 }}
             className="mb-12"
           >
             {owner?.profileImage ? (
               <div className="w-16 h-16 rounded-2xl bg-white/5 backdrop-blur-md p-1 border border-white/10 shadow-xl overflow-hidden">
                 <img src={owner.profileImage} alt={owner.name} className="w-full h-full object-cover rounded-xl" />
               </div>
             ) : (
               <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-800 to-black border border-white/10 flex items-center justify-center text-2xl font-bold shadow-xl">
                 {owner?.name?.charAt(0) || 'J'}
               </div>
             )}
           </motion.div>

           {/* Welcome Text */}
           <motion.div
             initial={{ y: 20, opacity: 0 }}
             animate={{ y: 0, opacity: 1 }}
             transition={{ delay: 0.3 }}
           >
             <h1 className="text-3xl font-bold mb-4 tracking-tight leading-tight">
               {selectedType ? selectedType.title : (owner?.welcomeMessage || "Book a meeting")}
             </h1>
             <p className="text-base text-gray-400 leading-relaxed max-w-sm">
               {selectedType ? selectedType.description : `Select a meeting type to get started with your booking${owner?.name ? ` with ${owner.name}` : ''}.`}
             </p>

             {selectedType && (
               <motion.div 
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 className="mt-8 space-y-4"
               >
                 <div className="flex items-center gap-3 text-gray-300">
                    <div className="p-2 rounded-lg bg-white/5 border border-white/5"><Clock className="w-4 h-4" /></div>
                    <span className="font-medium text-sm">{selectedType.durationMinutes} min</span>
                 </div>
                 <div className="flex items-center gap-3 text-gray-300">
                    <div className="p-2 rounded-lg bg-white/5 border border-white/5">
                      {selectedType.locationType === 'zoom' ? <Video className="w-4 h-4"/> : <MapPin className="w-4 h-4"/>}
                    </div>
                    <span className="font-medium capitalize text-sm">{selectedType.locationType.replace('_', ' ')}</span>
                 </div>
               </motion.div>
             )}
           </motion.div>
        </div>

        {/* Footer info - Updated to include JobMeet branding */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="relative z-10 flex items-center gap-3 text-xs font-medium text-gray-500 uppercase tracking-widest"
        >
          {owner?.company && (
             <div className="flex items-center gap-2">
               <Briefcase className="w-3 h-3" />
               <span>{owner.company}</span>
             </div>
          )}
          
          {owner?.company && <span className="w-1 h-1 bg-gray-700 rounded-full" />}
          
          <div className="flex items-center gap-1.5 opacity-60 hover:opacity-100 transition-opacity">
            <span className="w-1.5 h-1.5 bg-gray-600 rounded-full" />
            <span>JobMeet Scheduler</span>
          </div>
        </motion.div>
      </motion.div>

      {/* RIGHT PANEL: Interaction Area */}
      <div className="flex-1 p-8 md:p-12 relative bg-white h-full overflow-hidden flex flex-col">
        
        {/* Back Button - Fixed at top */}
        {step !== 'types' && step !== 'success' && (
          <div className="flex-shrink-0 mb-8">
            <button 
              onClick={() => {
                if (step === 'form') setStep('date')
                else if (step === 'date') {
                  if (selectedDate) setSelectedDate(null) // Back to calendar
                  else setStep('types')
                }
                else setStep('types')
              }}
              className="flex items-center gap-2 text-gray-400 hover:text-black transition-colors pl-2 group"
            >
              <div className="p-2 rounded-full bg-gray-50 group-hover:bg-gray-100 transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </div>
              <span className="font-semibold text-sm">Back</span>
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-3 border border-red-100 flex-shrink-0">
            <Info className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Main Content Area - Grow and Scroll */}
        <div className="flex-1 overflow-hidden relative">
          <AnimatePresence mode="wait">
            
            {/* STEP 1: Meeting Types */}
            {step === 'types' && (
              <motion.div 
                key="types"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="h-full flex flex-col overflow-y-auto pr-2 custom-scrollbar"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-8 tracking-tight">Select Meeting Type</h3>
                <div className="grid gap-4">
                  {meetingTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => { setSelectedType(type); setStep('date') }}
                      className="flex items-center justify-between p-6 rounded-2xl border border-gray-100 hover:border-black hover:shadow-xl hover:shadow-gray-200/50 transition-all group text-left bg-white relative overflow-hidden"
                    >
                      <div className="relative z-10">
                        <h4 className="font-bold text-lg text-gray-900 group-hover:text-black transition-colors">{type.title}</h4>
                        <p className="text-sm text-gray-500 mt-1 font-medium">{type.durationMinutes} min • {type.locationType.replace('_', ' ')}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all transform group-hover:scale-110 shadow-sm border border-gray-100 group-hover:border-black">
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* STEP 2: Date & Time - Split Views */}
            {step === 'date' && (
              <motion.div 
                key="date"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="h-full flex flex-col max-w-md mx-auto w-full"
              >
                {!selectedDate ? (
                  // VIEW A: Calendar Selection
                  <div className="flex flex-col h-full fade-in overflow-hidden">
                     <h3 className="text-2xl font-bold text-gray-900 mb-8 tracking-tight flex-shrink-0">Select a Date</h3>
                     <div className="flex-1 border border-gray-100 rounded-[2rem] overflow-hidden shadow-sm bg-white p-2 min-h-0">
                       {renderCalendar()}
                     </div>
                  </div>
                ) : (
                  // VIEW B: Time Slot Selection
                  <div className="flex flex-col h-full fade-in overflow-hidden">
                     <div className="mb-8 flex-shrink-0">
                       <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Select a Time</h3>
                       <p className="text-gray-500 font-medium mt-1">{format(selectedDate, 'EEEE, MMMM do, yyyy')}</p>
                     </div>
                     
                     <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                       {slotsLoading ? (
                         <div className="flex flex-col items-center justify-center h-64 gap-3">
                           <Loader2 className="animate-spin text-gray-900 w-8 h-8" />
                           <span className="text-sm font-medium text-gray-500">Checking availability...</span>
                         </div>
                       ) : slots.length > 0 ? (
                         <div className="grid grid-cols-2 gap-3 pb-4">
                           {slots.map((slot, i) => (
                             <button
                               key={i}
                               onClick={() => { setSelectedSlot(slot); setStep('form') }}
                               className="py-4 px-6 rounded-xl border border-gray-200 text-sm font-bold text-gray-900 hover:border-black hover:bg-black hover:text-white transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5"
                             >
                               {format(new Date(slot.startTs), 'h:mm a')}
                             </button>
                           ))}
                         </div>
                       ) : (
                         <div className="flex flex-col items-center justify-center h-64 text-center p-8 rounded-3xl bg-gray-50 border border-dashed border-gray-200">
                           <CalendarIcon className="w-10 h-10 text-gray-300 mb-3" />
                           <h4 className="font-bold text-gray-900 mb-1">No slots available</h4>
                           <p className="text-sm text-gray-500 mb-6">There are no available times on this date.</p>
                           <button 
                             onClick={() => setSelectedDate(null)}
                             className="text-sm font-bold text-black underline hover:text-gray-600 transition-colors"
                           >
                             Choose another date
                           </button>
                         </div>
                       )}
                     </div>
                  </div>
                )}
              </motion.div>
            )}

          {/* STEP 3: Form */}
          {step === 'form' && (
            <motion.div 
              key="form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-full flex flex-col max-w-md mx-auto w-full"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Your Details</h3>
              
              <div className="space-y-5 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                 <div className="grid grid-cols-2 gap-5">
                   {/* Name & Email inputs remain roughly same, but constrained by parent width */}
                   <div>
                     <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Your Name *</label>
                     <input 
                       className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:bg-white focus:border-gray-900 hover:bg-white hover:border-gray-200 transition-all outline-none text-sm font-medium"
                       placeholder="John Doe"
                       value={formData.name}
                       onChange={e => setFormData({...formData, name: e.target.value})}
                     />
                     {formErrors.name && <p className="text-xs text-red-500 font-medium mt-1">{formErrors.name}</p>}
                   </div>
                   <div>
                     <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                     <input 
                       className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:bg-white focus:border-gray-900 hover:bg-white hover:border-gray-200 transition-all outline-none text-sm font-medium"
                       placeholder="john@company.com"
                       value={formData.email}
                       onChange={e => setFormData({...formData, email: e.target.value})}
                     />
                     {formErrors.email && <p className="text-xs text-red-500 font-medium mt-1">{formErrors.email}</p>}
                   </div>
                 </div>

                 {/* Dynamic Questions */}
                 {smartForm ? (
                    smartForm.fields?.sort((a: any, b: any) => a.order - b.order).map((q: any) => (
                       <div key={q.id}>
                         <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{q.label} {q.required && '*'}</label>
                         {q.type === 'textarea' ? (
                           <textarea 
                             className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:bg-white focus:border-gray-900 hover:bg-white hover:border-gray-200 transition-all outline-none text-sm font-medium h-32 resize-none"
                             placeholder={q.placeholder}
                             value={formData.answers[q.id] || ''}
                             onChange={e => setFormData({
                               ...formData, 
                               answers: { ...formData.answers, [q.id]: e.target.value } 
                             })}
                           />
                         ) : (
                           <input 
                             className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:bg-white focus:border-gray-900 hover:bg-white hover:border-gray-200 transition-all outline-none text-sm font-medium"
                             placeholder={q.placeholder}
                             value={formData.answers[q.id] || ''}
                             onChange={e => setFormData({
                               ...formData, 
                               answers: { ...formData.answers, [q.id]: e.target.value } 
                             })}
                           />
                         )}
                         {formErrors[q.id] && <p className="text-xs text-red-500 font-medium mt-1">{formErrors[q.id]}</p>}
                       </div>
                   ))
                 ) : selectedType?.metadata?.formQuestions ? (
                    // Fallback using embedded metadata
                    selectedType.metadata.formQuestions.sort((a,b) => a.order - b.order).map(q => (
                       <div key={q.id}>
                         <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{q.label} {q.required && '*'}</label>
                         {q.type === 'textarea' ? (
                           <textarea 
                             className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:bg-white focus:border-gray-900 hover:bg-white hover:border-gray-200 transition-all outline-none text-sm font-medium h-32 resize-none"
                             placeholder={q.placeholder}
                             value={formData.answers[q.id] || ''}
                             onChange={e => setFormData({
                               ...formData, 
                               answers: { ...formData.answers, [q.id]: e.target.value } 
                             })}
                           />
                         ) : (
                           <input 
                             className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:bg-white focus:border-gray-900 hover:bg-white hover:border-gray-200 transition-all outline-none text-sm font-medium"
                             placeholder={q.placeholder}
                             value={formData.answers[q.id] || ''}
                             onChange={e => setFormData({
                               ...formData, 
                               answers: { ...formData.answers, [q.id]: e.target.value } 
                             })}
                           />
                         )}
                         {formErrors[q.id] && <p className="text-xs text-red-500 font-medium mt-1">{formErrors[q.id]}</p>}
                       </div>
                    ))
                 ) : null}
              </div>
              
              <div className="pt-6 mt-4 border-t border-gray-100">
                <Button onClick={submitBooking} disabled={loading} className="w-full py-4 text-base">
                   {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm Booking'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Success */}
          {step === 'success' && (
            <motion.div 
               key="success"
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               className="h-full flex flex-col items-center justify-center text-center px-8"
            >
               <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-6 shadow-xl shadow-green-200">
                 <Check className="w-10 h-10 text-white" />
               </div>
               <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Confirmed!</h2>
               <p className="text-gray-500 mb-8 max-w-sm">
                 You have successfully booked <strong>{selectedType?.title}</strong> with <strong>{owner?.name || 'us'}</strong>.
                 A confirmation email has been sent to {formData.email}.
               </p>
               
               <div className="w-full max-w-sm bg-gray-50 rounded-2xl p-6 border border-gray-100">
                 <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Powerful Scheduling</p>
                 <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white font-bold">J</div>
                    <div className="text-left">
                       <div className="font-bold text-gray-900">JobMeet Scheduler</div>
                       <div className="text-xs text-gray-500">Free for early adopters</div>
                    </div>
                 </div>
                 <a 
                   href="https://jobmeet.ai" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="block w-full py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
                 >
                   Create Your Free Page
                 </a>
               </div>
            </motion.div>
          )}

        </AnimatePresence>
        
        </div>
      </div>
    </div>
  )
}
